import { createContext, useContext, type ReactNode, type FC } from 'react';
import { type SupportedCurrencies } from 'dibs-currency-helpers/exports/currencies.ts';

type HomePageContextType = {
    isBot: boolean;
    isDesktop: boolean;
    isMobile: boolean;
    isTablet: boolean;
    isLoggedInUser: boolean;
    userType: string;
    priceBookName: string;
    isTrade: boolean;
    serverCurrency?: SupportedCurrencies;
    isTopDisplayAdEnabled?: boolean;
    isBottomDisplayAdEnabled?: boolean;
    isRemoveTopHeroBannerTestVariant?: boolean;
};

const HomePageContext = createContext<HomePageContextType>({
    isBot: false,
    isDesktop: false,
    isMobile: false,
    isTablet: false,
    isLoggedInUser: false,
    priceBookName: 'DEFAULT',
    isTrade: false,
    serverCurrency: 'USD',
    userType: '',
    isTopDisplayAdEnabled: false,
    isBottomDisplayAdEnabled: false,
    isRemoveTopHeroBannerTestVariant: false,
});

type ServerVarsProviderProps = {
    children: ReactNode;
} & HomePageContextType;

export const useHomePageContext = (): HomePageContextType => {
    const context = useContext(HomePageContext);
    if (!context) {
        throw new Error('useHomePageContext must be used within a HomePageContextProvider');
    }
    return context;
};

export const HomePageContextProvider: FC<ServerVarsProviderProps> = ({
    children,
    isBot,
    isDesktop,
    isMobile,
    isTablet,
    isLoggedInUser,
    userType,
    priceBookName,
    isTrade,
    serverCurrency = 'USD',
    isTopDisplayAdEnabled,
    isBottomDisplayAdEnabled,
    isRemoveTopHeroBannerTestVariant,
}) => {
    return (
        <HomePageContext
            value={{
                isBot,
                isMobile,
                isTablet,
                isDesktop,
                isLoggedInUser,
                userType,
                priceBookName,
                isTrade,
                serverCurrency,
                isTopDisplayAdEnabled,
                isBottomDisplayAdEnabled,
                isRemoveTopHeroBannerTestVariant,
            }}
        >
            {children}
        </HomePageContext>
    );
};
